import { useEffect, useRef } from 'react';

// Scroll-reveal hook: attaches an IntersectionObserver to a ref and adds
// the `is-visible` class (defined in index.css) when the element enters
// the viewport. Returns a ref to spread onto the target element.
//
// Usage:  const ref = useReveal();  <div ref={ref} className="reveal">…</div>
const useReveal = ({ threshold = 0.15, rootMargin = '0px 0px -8% 0px' } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      el.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
};

export default useReveal;
